// Session flow — orchestrates the answer/reveal loop
// (submit / dontKnow / nextCard / recordResult / showRevisionEmpty).
//
// The controller is a factory over injected deps: getters/setters into
// the shared `AppState` plus helpers (pickNextCard, pickDirection,
// estimateUserLevel, endDefi, renderStats) threaded through from
// bootstrap. This keeps the session module itself state-free and
// trivially testable.

import { GAME_MODES } from "../config/modes";
import { tts } from "../api/tts";
import { currentLocale, t } from "../i18n";
import { faceLang } from "../domain/direction";
import {
  answerMatches,
  displayAnswer,
  getExpectedText,
  getPromptText,
  matchableVariants,
  normalizeAnswer,
} from "../domain/text";
import { cardMatchesTagFilter } from "../state/settings";
import { tagFilterCount, type TagFilter } from "../config/tags";
import type { CardList } from "../config/lists";
import type {
  Card,
  CefrLevel,
  Direction,
  Lang,
  LevelCounts,
  StatsMap,
} from "../types";
import { buildAgreementHtml, renderCard as renderCardModule } from "./card";
import { byId } from "./dom";
import { escapeHtml, ttsButtonHtml } from "./html";

export type SessionDeps = {
  // State getters/setters — observe/mutate the shared AppState.
  getCards: () => Card[];
  getStats: () => StatsMap;
  getCurrent: () => Card | null;
  setCurrent: (c: Card | null) => void;
  getDirection: () => Direction;
  setDirection: (d: Direction) => void;
  getPhase: () => "answer" | "reveal";
  setPhase: (p: "answer" | "reveal") => void;
  getLastId: () => string | null;
  setLastId: (id: string | null) => void;
  getUserLevel: () => CefrLevel;
  setUserLevel: (l: CefrLevel) => void;
  setUserLevelCounts: (counts: LevelCounts) => void;
  getGameMode: () => string;
  /** Session counters — { attempts, correct, errors } mutated in place.
   *  `errors` is tracked regardless of gameMode so libre / revision
   *  end-of-session recaps can surface the same "à retravailler" list
   *  that défi has always shown. `hintedAttempts` / `hintedCorrect` are
   *  subsets tracking how many submissions used "Indice" — the recap
   *  shows a "dont X/Y avec indice" split. */
  getSession: () => {
    attempts: number;
    correct: number;
    errors: Array<{
      cardId: string;
      prompt: string;
      expected: string;
      userAnswer?: string;
    }>;
    hintedAttempts?: number;
    hintedCorrect?: number;
  };
  /** Défi state — null when not in défi mode. Errors live on session
   *  now, so défi state is just pacing/identity info. */
  getDefi: () => {
    total: number;
    seenIds?: Set<string>;
    done: boolean;
  } | null;
  getDirty: () => Set<string>;
  /** Active tag filter (class-axis, v1). Empty = no filter. */
  getTagFilter: () => TagFilter;
  /** The active deck config — threaded through so callers can resolve
   *  face-key → Lang for TTS voice picking without each one importing
   *  the catalog directly. */
  getList: () => CardList;
  /** Clear the filter + persist + re-render the header dropdown. Called
   *  by the inline "élargir le filtre" button in the empty state. */
  resetTagFilter: () => void;

  // Delegated helpers — bound to live state fields by bootstrap.
  pickNextCard: (
    cards: Card[],
    stats: StatsMap,
    lastId: string | null,
    userLevel: CefrLevel,
  ) => Card;
  pickDirection: () => Direction;
  estimateUserLevel: (
    cards: Card[],
    stats: StatsMap,
  ) => { level: CefrLevel; counts: LevelCounts };
  intervalForBox: (box: number) => number;
  endDefi: () => void;
  renderStats: () => void;

  // Stats persistence / sync.
  saveStats: (stats: StatsMap) => void;
  syncEnabled: () => boolean;
  scheduleSync: () => void;
  /** Per-deck TTS config — gates the ♪ speaker button (hidden when
   *  false) and all TTS firing. Read at render time so mid-session
   *  toggles inside deck-settings take effect immediately on the
   *  NEXT card without a reload. */
  isTtsEnabled: () => boolean;
  /** Only consulted when isTtsEnabled() is true. */
  isTtsAutoplay: () => boolean;
  /** Snapshot the current défi into localStorage so a refresh can
   *  resume via the boot-time modal. Called after every state-changing
   *  event in défi mode (new card picked, answer recorded). Optional —
   *  a controller without this is just non-resumable. */
  persistDefi?: () => void;
};

export type SessionController = {
  submit: () => void;
  dontKnow: () => void;
  hint: () => void;
  nextCard: () => void;
  /** Jump directly to a specific card — used by the search modal.
   *  Bypasses the normal pick-next pool logic so users can peek at any
   *  card regardless of tag filter or revision-due state. */
  goToCard: (card: Card) => void;
  recordResult: (card: Card, ok: boolean, assisted?: boolean) => void;
  showRevisionEmpty: (key: "revision_empty" | "revision_done") => void;
  resetHint: () => void;
};

export function createSessionController(deps: SessionDeps): SessionController {
  // Per-card flag: set when the user clicks "Indice" and consumed when
  // the card's answer is submitted. Correct-with-hint is neutral (no
  // stat change, no error recorded); wrong-with-hint falls through to
  // the normal wrong path.
  let hintUsed = false;

  function resetHint(): void {
    hintUsed = false;
    const hintEl = byId("hint");
    if (hintEl) {
      hintEl.classList.add("hidden");
      hintEl.classList.remove("bg");
      hintEl.textContent = "";
    }
    // Hint button available in every mode — défi recap surfaces a
    // separate "dont X/Y avec indice" split so hints don't distort the
    // main score.
    byId("hintBtn")?.classList.remove("hidden");
  }

  function renderCard(): void {
    renderCardModule({
      getCurrent: deps.getCurrent,
      getDirection: deps.getDirection,
      getList: deps.getList,
      setPhase: deps.setPhase,
      renderStats: deps.renderStats,
      isTtsEnabled: deps.isTtsEnabled,
    });
    resetHint();
  }

  // Toggle the real card vs the empty-state overlay. The overlay is a
  // sibling of .card so the real input/button event listeners survive
  // across empty <-> populated transitions.
  function setCardVisible(visible: boolean): void {
    const card = document.querySelector<HTMLElement>(".card:not(#cardEmptyState)");
    const empty = byId("cardEmptyState");
    if (card) card.classList.toggle("hidden", !visible);
    if (empty) empty.classList.toggle("hidden", visible);
  }

  function showRevisionEmpty(key: "revision_empty" | "revision_done"): void {
    const empty = byId("cardEmptyState");
    if (!empty) return;
    empty.innerHTML =
      `<div class="revision-empty">` +
      `<div class="revision-empty-icon">✓</div>` +
      `<div>${t(key)}</div>` +
      `</div>`;
    setCardVisible(false);
    const feedback = byId("feedback");
    if (feedback) {
      feedback.className = "feedback";
      feedback.innerHTML = "";
    }
  }

  // Same visual shell as showRevisionEmpty, plus an inline "reset filter"
  // action. Distinct function rather than an extended signature because
  // the button wiring is this-specific (calls back into nextCard after
  // clearing) and keeping it separate avoids leaking filter concerns
  // into the simpler revision-empty path.
  function showFilterEmpty(): void {
    const empty = byId("cardEmptyState");
    if (!empty) return;
    empty.innerHTML =
      `<div class="revision-empty">` +
      `<div class="revision-empty-icon">▽</div>` +
      `<div>${t("filter_empty")}</div>` +
      `<button type="button" class="btn ghost" id="filterResetInline">${t("filter_empty_action")}</button>` +
      `</div>`;
    setCardVisible(false);
    const feedback = byId("feedback");
    if (feedback) {
      feedback.className = "feedback";
      feedback.innerHTML = "";
    }
    const btn = byId<HTMLButtonElement>("filterResetInline");
    btn?.addEventListener("click", () => {
      deps.resetTagFilter();
      nextCard();
    });
  }

  function nextCard(): void {
    // Reset the empty-state overlay at each pick. The revision-empty /
    // filter-empty paths below will flip it back if they early-return;
    // otherwise the real card stays visible for renderCard to populate.
    setCardVisible(true);
    const session = deps.getSession();
    const mode = deps.getGameMode();
    const defi = deps.getDefi();
    // Défi mode: check if we've reached the card limit
    if (mode === "defi" && defi) {
      if (session.attempts >= defi.total) {
        deps.endDefi();
        return;
      }
    }

    deps.setDirection(deps.pickDirection());
    const dir = deps.getDirection();
    const cards = deps.getCards();
    const stats = deps.getStats();
    const est = deps.estimateUserLevel(cards, stats);
    deps.setUserLevel(est.level);
    deps.setUserLevelCounts(est.counts);

    // Cards that have BOTH the from and to lang texts
    const usable = cards.filter(
      (c) => c.faces[dir.from] && c.faces[dir.to],
    );
    let pool = usable.length ? usable : cards;

    // Revision mode: restrict to due cards only. Checked before the tag
    // filter so "no due cards at all" takes precedence over "filter
    // excludes everything" — telling the user to widen their filter
    // wouldn't actually help in that case.
    if (mode === "revision") {
      const now = Date.now();
      const duePool = pool.filter((c) => {
        const s = stats[c.id];
        if (!s || !s.attempts) return true; // never seen = due
        return (s.dueAt || 0) <= now;
      });
      if (duePool.length === 0) {
        showRevisionEmpty(session.attempts > 0 ? "revision_done" : "revision_empty");
        return;
      }
      pool = duePool;
    }

    // Tag filter (class / domain / register axes — AND between axes,
    // OR within). Applied after revision's due-filter so the filter-
    // empty state is distinct from revision-empty, and shown with a
    // reset-filter action rather than a generic checkmark.
    const filter = deps.getTagFilter();
    if (tagFilterCount(filter) > 0) {
      const filtered = pool.filter((c) => cardMatchesTagFilter(c.tags, filter));
      if (filtered.length === 0) {
        showFilterEmpty();
        return;
      }
      pool = filtered;
    }

    // Défi mode: exclude cards already seen in this défi session
    if (mode === "defi" && defi && defi.seenIds) {
      const unseen = pool.filter((c) => !defi.seenIds!.has(c.id));
      if (unseen.length) pool = unseen;
    }

    const picked = deps.pickNextCard(pool, stats, deps.getLastId(), est.level);
    deps.setCurrent(picked || null);
    if (picked) {
      deps.setLastId(picked.id);
      if (mode === "defi" && defi && defi.seenIds) {
        defi.seenIds.add(picked.id);
        // Persist after the new card is added to seenIds — a refresh
        // now would resume knowing this card has been shown.
        deps.persistDefi?.();
      }
    }
    renderCard();
  }

  function recordResult(card: Card, ok: boolean, assisted = false): void {
    const id = card.id;
    const now = Date.now();
    const stats = deps.getStats();
    // Correct-with-hint leaves per-card stats untouched — we don't want
    // hint-assisted answers to advance the Leitner box or inflate the
    // CEFR-level estimate. Wrong-with-hint still penalizes normally.
    const skipCardStats = ok && assisted;
    if (!skipCardStats) {
      const s =
        stats[id] ||
        ({
          attempts: 0,
          correct: 0,
          box: 1,
          dueAt: 0,
          lastSeenAt: 0,
          lastFailAt: 0,
        } as import("../types").CardStat);
      // Migrate legacy stats: ensure box/dueAt exist
      if (s.box === undefined) s.box = 1;
      if (s.dueAt === undefined) s.dueAt = 0;
      const wasDue = (s.dueAt || 0) <= now;
      s.attempts++;
      if (ok) {
        s.correct++;
        // Only advance the box if the card was actually due. Reviewing
        // ahead of schedule doesn't promote the card.
        if (wasDue) {
          s.box = Math.min((s.box || 1) + 1, 5);
          s.dueAt = now + deps.intervalForBox(s.box);
        }
      } else {
        s.lastFailAt = now;
        s.box = 1;
        s.dueAt = now; // back in rotation immediately
      }
      s.lastSeenAt = now;
      stats[id] = s;
    }
    // Session counters — per page-load, for the "session" stat pill and
    // the défi pacing loop. Every submission counts (hint or not) so the
    // défi total actually advances; the hinted subset is tracked
    // separately for the recap split.
    const session = deps.getSession();
    session.attempts++;
    if (ok) session.correct++;
    if (assisted) {
      session.hintedAttempts = (session.hintedAttempts ?? 0) + 1;
      if (ok) session.hintedCorrect = (session.hintedCorrect ?? 0) + 1;
    }
    if (!skipCardStats) {
      deps.saveStats(stats);
      if (deps.syncEnabled()) {
        deps.getDirty().add(id);
        deps.scheduleSync();
      }
    }
  }

  function ttsBtn(text: string, lang: Lang): string {
    // TTS is per-deck now — hide the ♪ speaker entirely on decks that
    // opted out (the default for mono geography decks: hearing "Paris"
    // pronounced in the user's own language adds nothing).
    if (!deps.isTtsEnabled()) return "";
    return ttsButtonHtml(text, lang, t("tts_play") as string);
  }

  /** Show the inline 🔊 inside the answer input on a correct answer.
   *  Populates the data-tts-* attrs the global click delegate reads;
   *  the input gets a `.has-tts` helper class on the wrap so the right
   *  padding clears room for the button. */
  function showInlineAnswerTts(text: string, lang: Lang): void {
    const btn = byId<HTMLButtonElement>("answerInputTts");
    const wrap = btn?.parentElement;
    if (!btn || !wrap) return;
    btn.dataset.ttsText = text;
    btn.dataset.ttsLang = lang;
    btn.title = t("tts_play") as string;
    btn.setAttribute("aria-label", t("tts_play") as string);
    btn.hidden = false;
    wrap.classList.add("has-tts");
  }

  function maybeAutoplay(text: string, lang: Lang): void {
    // Both gates: master must be on AND autoplay must be on. Master
    // off hides the speaker anyway, so autoplay-only is meaningless.
    if (!deps.isTtsEnabled() || !deps.isTtsAutoplay()) return;
    if (!tts.isSupported() || !tts.hasVoice(lang)) return;
    tts.speak(text, lang);
  }

  function finishWithFeedback(
    card: Card,
    expected: string,
    ok: boolean,
    userAnswer: string,
  ): void {
    const dir = deps.getDirection();
    const list = deps.getList();
    const toLang = faceLang(dir.to, list, currentLocale());
    const agreementHtml = buildAgreementHtml(card, dir);
    const answerInput = byId<HTMLInputElement>("answerInput");
    const feedback = byId("feedback");
    const submitBtn = byId("submitBtn");
    const dontKnowBtn = byId("dontKnowBtn");
    const expectedDisplay = displayAnswer(expected);

    if (ok) {
      answerInput?.classList.add("correct");
      const variants = matchableVariants(expected);
      const userNorm = normalizeAnswer(userAnswer);
      const synonyms = variants.filter((v) => normalizeAnswer(v) !== userNorm);
      let fbHtml = "";
      if (synonyms.length) {
        const synText = synonyms.join(" / ");
        fbHtml += `<div class="label">${t("fb_synonym")}</div><div class="answer">${escapeHtml(synText)}${ttsBtn(synText, toLang)}</div>`;
      } else if (tts.isSupported() && tts.hasVoice(toLang)) {
        // Correct without synonyms: mount a 🔊 inside the answer input
        // itself (the user's text is already the right word, so a
        // duplicate feedback row below would just be visual noise).
        showInlineAnswerTts(expectedDisplay, toLang);
      }
      fbHtml += agreementHtml;
      if (feedback) {
        if (fbHtml) {
          feedback.className = "feedback show neutral";
          feedback.innerHTML = fbHtml;
        } else {
          feedback.className = "feedback";
          feedback.innerHTML = "";
        }
      }
      maybeAutoplay(expectedDisplay, toLang);
    } else {
      answerInput?.classList.add("wrong");
      if (feedback) {
        feedback.className = "feedback show neutral";
        feedback.innerHTML =
          `<div class="label">${t("fb_expected")}</div><div class="answer">${escapeHtml(expectedDisplay)}${ttsBtn(expectedDisplay, toLang)}</div>` +
          agreementHtml;
      }
      maybeAutoplay(expectedDisplay, toLang);
    }
    answerInput?.setAttribute("readonly", "readonly");
    if (submitBtn) submitBtn.textContent = t("btn_next") as string;
    dontKnowBtn?.classList.add("hidden");
    byId("hintBtn")?.classList.add("hidden");
    deps.setPhase("reveal");
    deps.renderStats();
    // After "Je ne sais pas" the click landed on the (now-hidden)
    // dontKnowBtn, stranding focus on document.body — Enter then
    // fires nowhere. Park focus on the submit button so the user can
    // just keep pressing Enter to step through the session.
    (submitBtn as HTMLButtonElement | null)?.focus();
  }

  function submit(): void {
    if (deps.getPhase() === "reveal") {
      const mode = deps.getGameMode();
      const defi = deps.getDefi();
      const session = deps.getSession();
      if (mode === "defi" && defi && session.attempts >= defi.total) {
        deps.endDefi();
        return;
      }
      nextCard();
      return;
    }
    const c = deps.getCurrent();
    if (!c) return;
    const answerInput = byId<HTMLInputElement>("answerInput");
    if (!answerInput) return;
    // Empty answer = "don't know" — treat as failure with neutral label.
    if (!answerInput.value.trim()) {
      dontKnow();
      return;
    }
    const dir = deps.getDirection();
    const expected = getExpectedText(c, dir);
    const ok = answerMatches(answerInput.value, expected);
    recordResult(c, ok, hintUsed);
    // Track errors on every mode — libre / revision end-recaps surface
    // the same list as défi. Dedup by cardId so the same card failing
    // twice in a session shows up once. Capture the wrong attempt so
    // the recap can show "you typed X, expected Y" side-by-side.
    if (!ok) {
      const sess = deps.getSession();
      if (!sess.errors.some((e) => e.cardId === c.id)) {
        sess.errors.push({
          cardId: c.id,
          prompt: getPromptText(c, dir),
          expected: displayAnswer(expected),
          userAnswer: answerInput.value,
        });
      }
    }
    if (deps.getGameMode() === "defi") {
      // Snapshot the défi after the answer — captures the updated
      // session counters + any new error. Works for both correct and
      // wrong answers (a refresh shouldn't rewind correct answers
      // either).
      deps.persistDefi?.();
    }
    finishWithFeedback(c, expected, ok, answerInput.value);
  }

  /** Reveal the first letter of the expected answer as a masked pattern
   *  (`к _ _ _`). Marks the card as hinted so a subsequent correct
   *  submission doesn't count toward mastery. One hint per card;
   *  disabled in défi mode (resetHint hides the button there). */
  function hint(): void {
    if (deps.getPhase() === "reveal") return;
    if (hintUsed) return;
    const c = deps.getCurrent();
    if (!c) return;
    const dir = deps.getDirection();
    const expected = getExpectedText(c, dir);
    // matchableVariants strips parentheticals first — the parens carry
    // visual mnemonics ("Vendée (85)", "France (F)") that must NOT
    // appear in the masked hint, otherwise the user sees the answer
    // they're trying to recall (the code) revealed for free.
    const first = matchableVariants(expected)[0] || expected;
    if (!first) return;
    const chars = Array.from(first);
    const mask = chars
      .map((ch, i) => {
        if (i === 0) return ch;
        if (ch === " ") return " ";
        return "_";
      })
      .join(" ");
    const hintEl = byId("hint");
    if (hintEl) {
      hintEl.textContent = mask;
      hintEl.classList.remove("hidden");
      hintEl.classList.toggle("bg", dir.to === "bg-BG");
    }
    const hintBtn = byId("hintBtn");
    hintBtn?.classList.add("hidden");
    hintUsed = true;
    // Keep focus on the input so the user can keep typing without a
    // manual click after revealing the hint.
    const answerInput = byId<HTMLInputElement>("answerInput");
    answerInput?.focus();
  }

  function dontKnow(): void {
    if (deps.getPhase() === "reveal") return;
    const c = deps.getCurrent();
    if (!c) return;
    const dir = deps.getDirection();
    const expected = getExpectedText(c, dir);
    const agreementHtml = buildAgreementHtml(c, dir);
    recordResult(c, false, hintUsed);
    // Track errors on every mode — dedup by cardId.
    const sess = deps.getSession();
    if (!sess.errors.some((e) => e.cardId === c.id)) {
      sess.errors.push({
        cardId: c.id,
        prompt: getPromptText(c, dir),
        expected: displayAnswer(expected),
      });
    }
    if (deps.getGameMode() === "defi") {
      // Persist after the state change so a refresh sees the error
      // already registered.
      deps.persistDefi?.();
    }
    const answerInput = byId<HTMLInputElement>("answerInput");
    const feedback = byId("feedback");
    const submitBtn = byId("submitBtn");
    const dontKnowBtn = byId("dontKnowBtn");
    const expectedDisplay = displayAnswer(expected);
    const toLang = faceLang(dir.to, deps.getList(), currentLocale());
    // Visually treat "I don't know" as a wrong answer (red outline)
    answerInput?.classList.add("wrong");
    if (feedback) {
      feedback.className = "feedback show neutral";
      feedback.innerHTML =
        `<div class="label">${t("fb_answer")}</div><div class="answer">${escapeHtml(expectedDisplay)}${ttsBtn(expectedDisplay, toLang)}</div>` +
        agreementHtml;
    }
    maybeAutoplay(expectedDisplay, toLang);
    answerInput?.setAttribute("readonly", "readonly");
    if (submitBtn) submitBtn.textContent = t("btn_next") as string;
    dontKnowBtn?.classList.add("hidden");
    byId("hintBtn")?.classList.add("hidden");
    deps.setPhase("reveal");
    deps.renderStats();
    // After "Je ne sais pas" the click landed on the (now-hidden)
    // dontKnowBtn, stranding focus on document.body — Enter then
    // fires nowhere. Park focus on the submit button so the user can
    // just keep pressing Enter to step through the session.
    (submitBtn as HTMLButtonElement | null)?.focus();
  }

  function goToCard(card: Card): void {
    // Make the target card visible (in case an empty-state overlay was
    // showing) and render it immediately. We leave the tag filter and
    // revision-due rules intact — this is a peek, not a pool change.
    setCardVisible(true);
    deps.setCurrent(card);
    deps.setLastId(card.id);
    deps.setDirection(deps.pickDirection());
    renderCard();
    // Looking up a card via search implies the user doesn't remember
    // it — surface the answer immediately and count the peek as a miss
    // so the Leitner box resets and the card comes back in rotation.
    dontKnow();
  }

  return {
    submit,
    dontKnow,
    hint,
    nextCard,
    goToCard,
    recordResult,
    showRevisionEmpty,
    resetHint,
  };
}
